/**
 * TruePrice V1 - Career Stage Types
 * 
 * Defines the career stages and their market multipliers.
 * Source: SSOT, Addendum B (V1 Scope), Addendum E (Calibration Gate)
 */

export const CAREER_STAGE_KEYS = ['unlisted', 'emerging', 'working', 'established'] as const;

// Note: 'investable' exists in schema but hidden in V1 UI (Addendum B Section 3.1)
export const CAREER_STAGE_KEYS_ALL = [...CAREER_STAGE_KEYS, 'investable'] as const;

export type CareerStageKey = typeof CAREER_STAGE_KEYS[number];
export type CareerStageKeyAll = typeof CAREER_STAGE_KEYS_ALL[number];

export interface CareerStageConfig {
  key: CareerStageKey;
  label: string;
  multiplier: number;           // C_stage - Market ceiling multiplier
  description: string;
  minimumHighestSale: number;   // Calibration gate threshold (cents)
}

export const CAREER_STAGES: Record<CareerStageKey, CareerStageConfig> = {
  unlisted: {
    key: 'unlisted',
    label: 'Unlisted Artist',
    multiplier: 0.6,
    description: "You're building your first body of work. You haven't sold an original yet, and that's okay — everyone starts here.",
    minimumHighestSale: 0,
  },
  emerging: {
    key: 'emerging',
    label: 'Emerging Artist',
    multiplier: 1.0,
    description: "You've made your first sales and are building momentum. Collectors are starting to notice your work.",
    minimumHighestSale: 2500, // $25 minimum to count as a real sale
  },
  working: {
    key: 'working',
    label: 'Working Artist',
    multiplier: 1.5,
    description: "Art is a real part of your income. You regularly sell pieces in the $500–$2,000 range and have repeat collectors.",
    minimumHighestSale: 50000, // $500 in cents
  },
  established: {
    key: 'established',
    label: 'Established Artist',
    multiplier: 2.5,
    description: "You have a proven track record. Collectors seek you out, and your work consistently sells above $2,000.",
    minimumHighestSale: 200000, // $2,000 in cents
  },
};

// Hidden in V1, but kept for schema compatibility
export const INVESTABLE_STAGE = {
  key: 'investable' as const,
  label: 'Investable Artist',
  multiplier: 4.0,
  description: 'Blue chip status. Secondary market activity, major gallery representation, and institutional collectors.',
  minimumHighestSale: 1000000, // $10,000 in cents (V2 threshold TBD)
};

export function isCareerStageKey(value: string): value is CareerStageKey {
  return CAREER_STAGE_KEYS.includes(value as CareerStageKey);
}

export function getCareerStageConfig(key: CareerStageKey): CareerStageConfig {
  return CAREER_STAGES[key];
}

/**
 * Determine the maximum career stage a user can select based on their highest sale
 * Source: Addendum E Section 2.3 (Calibration Gate)
 */
export function getMaxAllowedStage(highestSaleCents: number | null): CareerStageKey {
  const sale = highestSaleCents ?? 0;
  
  if (sale >= 200000) return 'established';  // $2,000+
  if (sale >= 50000) return 'working';       // $500+
  if (sale >= 2500) return 'emerging';       // $25+ (real sale threshold)
  return 'unlisted';                          // No meaningful sales
}

/**
 * Check if a career stage selection is valid given highest sale
 * Returns the stage they should be downgraded to if invalid
 */
export function validateCareerStage(
  selectedStage: CareerStageKey,
  highestSaleCents: number | null
): { valid: boolean; allowedStage: CareerStageKey } {
  const maxAllowed = getMaxAllowedStage(highestSaleCents);
  const selectedMultiplier = CAREER_STAGES[selectedStage].multiplier;
  const maxMultiplier = CAREER_STAGES[maxAllowed].multiplier;
  
  if (selectedMultiplier <= maxMultiplier) {
    return { valid: true, allowedStage: selectedStage };
  }
  
  return { valid: false, allowedStage: maxAllowed };
}

/**
 * Check if a new sale unlocks a higher career stage
 * Returns the newly unlocked stage if applicable
 */
export function checkStageUnlock(
  currentHighestSale: number | null,
  newSaleCents: number
): CareerStageKey | null {
  const previousMax = getMaxAllowedStage(currentHighestSale);
  const newMax = getMaxAllowedStage(newSaleCents);
  
  if (CAREER_STAGES[newMax].multiplier > CAREER_STAGES[previousMax].multiplier) {
    return newMax;
  }
  
  return null;
}
